import { useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Button, Badge, Modal, Input, toast } from '../../../ui';
import { LoadingSpinner } from '../../../components/common/LoadingSpinner';
import {
  useCardInvoice,
  usePatchInvoiceItem,
  useAddInvoiceItem,
  useApproveInvoice,
  useUnapproveInvoice,
  useRejectAllInvoice,
  useUnlinkSettlement,
  useCloseInvoice,
  useDeleteCardInvoice,
} from '../hooks/useCardInvoices';
import { useCategories, useCompanies } from '../../../hooks/useFinancialOps';
import { ConciliarFaturaDialog } from '../components/ConciliarFaturaDialog';
import { formatCurrency, formatDateBR } from '../../../utils/format';
import { competenciaLabel } from '../utils/competencia';
import { FaturaOrigemBadges } from '../components/FaturaOrigemBadges';
import type { CardInvoiceItemDTO, CardInvoiceItemKind } from '../services/card-invoices.service';

const KIND_LABELS: Record<CardInvoiceItemKind, string> = {
  PURCHASE: 'Compra',
  INSTALLMENT: 'Parcela',
  FEE: 'Tarifa',
  IOF: 'IOF',
  INTEREST: 'Juros',
  CREDIT: 'Estorno',
  ADJUSTMENT: 'Ajuste',
};

const ITEM_STATUS_TONE: Record<string, 'neutral' | 'accent' | 'warning'> = {
  PENDING: 'warning',
  APPROVED: 'accent',
  REJECTED: 'neutral',
};

const ITEM_STATUS_LABELS: Record<string, string> = {
  PENDING: 'Pendente',
  APPROVED: 'Aprovado',
  REJECTED: 'Rejeitado',
};

/** Item re-ancorado devolvido por `approve` — competência de origem e destino. */
type DeferredEntry = {
  itemId: string;
  from: { month: number; year: number };
  to: { month: number; year: number };
};

/**
 * `unapprove` devolve 200 com os erros por item — sem traduzir, o usuário lê
 * "aprovação desfeita" para uma linha que o backend recusou.
 */
const UNAPPROVE_ERROR_LABELS: Record<string, string> = {
  COMPETENCE_CLOSED: 'a competência do lançamento já está finalizada — reabra o fechamento antes',
  CHARGE_SETTLED: 'o rateio já foi quitado — estorne a quitação antes',
  NOT_APPROVED: 'o item não está aprovado',
  NOT_FOUND: 'item não encontrado',
};

const INVOICE_STATUS_LABELS: Record<string, string> = {
  PENDING: 'Pendente',
  PROCESSING: 'Processando',
  EXTRACTED: 'Em revisão',
  RECONCILING: 'Aguardando conciliação',
  CLOSED: 'Conciliada',
  FAILED: 'Falhou',
};

export function FaturaReviewPage() {
  const { id = '' } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data, isLoading, isError } = useCardInvoice(id);
  const invoice = data?.data?.invoice;
  const items: CardInvoiceItemDTO[] = data?.data?.items ?? [];

  const { data: categoriesData } = useCategories();
  const categories = categoriesData?.data ?? [];

  // Rateio: só faz sentido apontar para uma empresa DIFERENTE da do cartão.
  const { data: companiesData } = useCompanies({ active: true });
  const otherCompanies = useMemo(
    () =>
      ((companiesData?.data ?? []) as Array<{ id: string; name: string }>).filter(
        (c) => c.id !== invoice?.companyId
      ),
    [companiesData, invoice?.companyId]
  );

  const patch = usePatchInvoiceItem(id);
  const addItem = useAddInvoiceItem(id);
  const approve = useApproveInvoice(id);
  const unapprove = useUnapproveInvoice(id);
  const rejectAll = useRejectAllInvoice(id);
  const unlink = useUnlinkSettlement(id);
  const closeInvoice = useCloseInvoice(id);
  const deleteInvoice = useDeleteCardInvoice();
  const [showConciliar, setShowConciliar] = useState(false);
  const settlement = data?.data?.settlementTransaction ?? null;
  const deltaCents = data?.data?.deltaCents ?? null;

  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [showAddModal, setShowAddModal] = useState(false);
  const [manualForm, setManualForm] = useState({
    postDate: new Date().toISOString().slice(0, 10),
    description: '',
    amount: '',
    kind: 'ADJUSTMENT' as CardInvoiceItemKind,
  });

  const pendingItems = useMemo(() => items.filter((i) => i.status === 'PENDING'), [items]);

  // Painel da revisão (§6 passo 3): Σ itens pendentes+aprovados (EXPENSE soma, INCOME subtrai).
  const stagedSum = useMemo(
    () =>
      items
        .filter((i) => i.status !== 'REJECTED')
        .reduce((acc, i) => acc + (i.type === 'EXPENSE' ? i.amount : -i.amount), 0),
    [items]
  );

  function toggle(itemId: string) {
    const next = new Set(selected);
    if (next.has(itemId)) {
      next.delete(itemId);
    } else {
      next.add(itemId);
    }
    setSelected(next);
  }

  async function runApprove(ids: string[]) {
    if (ids.length === 0) {
      toast.error('Selecione pelo menos um item.');
      return;
    }
    try {
      const r = await approve.mutateAsync({ itemIds: ids });
      const out = r?.data ?? r;
      toast.success(`${out.approved} item(ns) aprovado(s)`);
      if (out.errors?.length) {
        toast.error(`${out.errors.length} erro(s) — verifique a lista`);
      }
      if (out.deferred?.length) {
        // Duas empresas com fechamentos diferentes produzem destinos diferentes
        // no mesmo lote — o primeiro item não fala pelos outros.
        const destinos = [
          ...new Set(out.deferred.map((d: DeferredEntry) => competenciaLabel(d.to))),
        ];
        const origens = [...new Set(out.deferred.map((d: any) => competenciaLabel(d.from)))].join(
          ', '
        );
        const destino =
          destinos.length === 1
            ? `na competência ${destinos[0]}`
            : `nas competências ${destinos.join(', ')}`;
        toast.info(
          `${out.deferred.length} item(ns) de ${origens} entraram ${destino} — ` +
            `o fechamento de origem já estava finalizado.`
        );
      }
      setSelected(new Set());
    } catch (err: any) {
      toast.error(err?.response?.data?.message || err?.message || 'Falha ao aprovar');
    }
  }

  async function handleAddItem(e: React.FormEvent) {
    e.preventDefault();
    const amount = Number(manualForm.amount);
    if (!manualForm.description || !amount || amount <= 0) {
      toast.error('Informe descrição e valor positivo.');
      return;
    }
    try {
      await addItem.mutateAsync({
        postDate: manualForm.postDate,
        description: manualForm.description,
        amount,
        kind: manualForm.kind,
      });
      toast.success('Item adicionado — aprove-o para entrar na soma.');
      setShowAddModal(false);
      setManualForm({
        postDate: new Date().toISOString().slice(0, 10),
        description: '',
        amount: '',
        kind: 'ADJUSTMENT',
      });
    } catch (err: any) {
      toast.error(err?.response?.data?.message || err?.message || 'Falha ao adicionar item');
    }
  }

  async function handleUnapprove(itemId: string) {
    try {
      const r = await unapprove.mutateAsync({ itemIds: [itemId] });
      const out = r?.data ?? r;
      if (out?.errors?.length) {
        const motivos = [
          ...new Set(
            out.errors.map(
              (e: { code: string }) => UNAPPROVE_ERROR_LABELS[e.code] ?? String(e.code)
            )
          ),
        ].join('; ');
        toast.error(`Não foi possível desfazer a aprovação: ${motivos}.`);
        return;
      }
      toast.success('Aprovação desfeita — o lançamento gerado foi removido.');
    } catch (err: any) {
      toast.error(err?.response?.data?.message || err?.message || 'Falha ao desfazer aprovação');
    }
  }

  async function handleRestore(itemId: string) {
    try {
      await patch.mutateAsync({ itemId, patch: { status: 'PENDING' } });
      toast.success('Item restaurado para revisão.');
    } catch (err: any) {
      toast.error(err?.response?.data?.message || err?.message || 'Falha ao restaurar item');
    }
  }

  async function handleDeleteInvoice() {
    if (
      !confirm(
        'Excluir esta fatura? Os itens e os lançamentos gerados por aprovação serão removidos. O débito bancário do pagamento (se houver) NÃO é apagado, apenas desvinculado.'
      )
    ) {
      return;
    }
    try {
      await deleteInvoice.mutateAsync(id);
      toast.success('Fatura excluída.');
      navigate('/financeiro?tab=faturas');
    } catch (err: any) {
      toast.error(err?.response?.data?.message || err?.message || 'Falha ao excluir a fatura');
    }
  }

  async function handleClose() {
    try {
      await closeInvoice.mutateAsync();
      toast.success('Fatura conciliada e fechada.');
    } catch (err: any) {
      const body = err?.response?.data;
      if (err?.response?.status === 422 && body?.delta !== undefined) {
        const delta = Number(body.delta);
        toast.error(
          delta > 0
            ? `Faltam ${formatCurrency(delta)} em lançamentos — adicione a linha de IOF/juros/encargo que está na fatura ou corrija valores.`
            : `Os itens excedem o pagamento em ${formatCurrency(Math.abs(delta))} — item duplicado/valor errado ou pagamento parcial.`
        );
        return;
      }
      toast.error(body?.message || err?.message || 'Falha ao fechar a fatura.');
    }
  }

  if (isLoading) {
    return <LoadingSpinner />;
  }
  if (isError || !invoice) {
    return <div className="p-6 text-ink-secondary">Fatura não encontrada.</div>;
  }

  if (invoice.status === 'FAILED') {
    return (
      <div className="p-6 space-y-2">
        <h1 className="text-xl font-semibold">Extração da fatura falhou</h1>
        <p className="text-ink-secondary">{invoice.errorMessage}</p>
        <div className="flex items-center gap-3">
          <Button variant="ghost" disabled={deleteInvoice.isPending} onClick={handleDeleteInvoice}>
            Excluir fatura
          </Button>
          <Link to="/financeiro?tab=faturas" className="text-link">
            Voltar
          </Link>
        </div>
      </div>
    );
  }

  const gap = invoice.totalAmount - stagedSum;
  const allPendingIds = pendingItems.map((i) => i.id);

  return (
    <div className="p-6 space-y-4">
      <header className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold">
            Fatura {String(invoice.referenceMonth).padStart(2, '0')}/{invoice.referenceYear} —{' '}
            {invoice.card?.name}
          </h1>
          <p className="text-sm text-ink-secondary">
            {invoice.fileName}
            {invoice.dueDate ? ` · vencimento ${formatDateBR(invoice.dueDate)}` : ''}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <FaturaOrigemBadges invoice={invoice} />
          <Badge tone={invoice.status === 'CLOSED' ? 'accent' : 'warning'}>
            {INVOICE_STATUS_LABELS[invoice.status] ?? invoice.status}
          </Badge>
          {invoice.status !== 'CLOSED' && (
            <Button
              variant="ghost"
              disabled={deleteInvoice.isPending}
              onClick={handleDeleteInvoice}
            >
              Excluir fatura
            </Button>
          )}
        </div>
      </header>

      <p className="text-xs text-ink-tertiary">
        Gastos entram na DRE do mês da compra; o pagamento aparece no caixa do mês do débito
        (competência vs caixa — comportamento esperado).
      </p>

      {invoice.status === 'RECONCILING' && (
        <div className="border border-warning/50 bg-warning/10 text-sm rounded p-3">
          Fatura não conciliada — a DRE deste mês pode estar dobrando o pagamento do cartão até você
          concluir a conciliação abaixo.
        </div>
      )}

      {(invoice.status === 'RECONCILING' || invoice.status === 'CLOSED') && (
        <div className="border border-border rounded-lg p-4 space-y-3">
          <h2 className="font-semibold text-sm">Conciliação do pagamento</h2>
          {settlement ? (
            <div className="text-sm space-y-2">
              <p>
                Débito vinculado: <strong>{settlement.description}</strong> ·{' '}
                {formatCurrency(settlement.amount)} · {formatDateBR(settlement.transactionDate)}
              </p>
              <p>
                Delta (pagamento − Σ itens aprovados):{' '}
                <strong
                  className={
                    deltaCents !== null && Math.abs(deltaCents) <= 1 ? 'text-accent' : 'text-danger'
                  }
                >
                  {deltaCents === null ? '—' : formatCurrency(deltaCents / 100)}
                </strong>
              </p>
              {invoice.status === 'RECONCILING' && (
                <div className="flex gap-2">
                  <Button
                    onClick={handleClose}
                    disabled={
                      closeInvoice.isPending || deltaCents === null || Math.abs(deltaCents) > 1
                    }
                  >
                    Fechar fatura
                  </Button>
                  <Button
                    variant="ghost"
                    disabled={unlink.isPending}
                    onClick={async () => {
                      await unlink.mutateAsync();
                      toast.success('Pagamento desvinculado.');
                    }}
                  >
                    Desvincular
                  </Button>
                </div>
              )}
              {invoice.status === 'CLOSED' && (
                <p className="text-accent text-xs">
                  Fatura fechada{invoice.closedAt ? ` em ${formatDateBR(invoice.closedAt)}` : ''} —
                  delta congelado: {formatCurrency(invoice.reconciledDelta ?? 0)}.
                </p>
              )}
            </div>
          ) : (
            <div className="space-y-2 text-sm">
              <p className="text-ink-secondary">
                Nenhum débito bancário vinculado ainda — este é o próximo passo.
              </p>
              <Button onClick={() => setShowConciliar(true)}>Conciliar pagamento</Button>
            </div>
          )}
        </div>
      )}

      <div className="border border-border rounded-lg p-4 grid grid-cols-3 gap-4 text-sm">
        <div>
          <p className="text-ink-tertiary text-xs uppercase">Total da fatura (extraído)</p>
          <p className="font-semibold">{formatCurrency(invoice.totalAmount)}</p>
        </div>
        <div>
          <p className="text-ink-tertiary text-xs uppercase">Σ itens (pendentes + aprovados)</p>
          <p className="font-semibold">{formatCurrency(stagedSum)}</p>
        </div>
        <div>
          <p className="text-ink-tertiary text-xs uppercase">Gap</p>
          <p className={`font-semibold ${Math.abs(gap) > 0.01 ? 'text-danger' : 'text-accent'}`}>
            {formatCurrency(gap)}
          </p>
        </div>
      </div>

      {invoice.status === 'EXTRACTED' && (
        <div className="flex gap-2 flex-wrap">
          <Button
            disabled={!selected.size || approve.isPending}
            onClick={() => runApprove(Array.from(selected))}
          >
            Aprovar selecionados
          </Button>
          <Button
            variant="ghost"
            disabled={!allPendingIds.length || approve.isPending}
            onClick={() => runApprove(allPendingIds)}
          >
            Aprovar todos
          </Button>
          <Button variant="ghost" onClick={() => setShowAddModal(true)}>
            Adicionar linha
          </Button>
          <Button
            variant="ghost"
            disabled={!allPendingIds.length || rejectAll.isPending}
            onClick={async () => {
              if (!confirm('Rejeitar todos os itens pendentes?')) {
                return;
              }
              await rejectAll.mutateAsync();
              toast.success('Itens pendentes rejeitados.');
            }}
          >
            Rejeitar todos
          </Button>
        </div>
      )}

      {invoice.status === 'RECONCILING' && (
        <div className="flex gap-2 flex-wrap">
          <Button variant="ghost" onClick={() => setShowAddModal(true)}>
            Adicionar linha (volta para revisão)
          </Button>
        </div>
      )}

      <table className="w-full text-sm">
        <thead className="text-left text-ink-tertiary">
          <tr>
            <th className="py-2"></th>
            <th>Data da compra</th>
            <th>Descrição</th>
            <th>Tipo de linha</th>
            <th className="text-right pr-2">Valor</th>
            <th>Categoria</th>
            <th>Gasto de</th>
            <th>Status</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {items.map((item) => {
            const isPending = item.status === 'PENDING';
            return (
              <tr key={item.id} className="border-t border-border">
                <td className="py-2">
                  {isPending && (
                    <input
                      type="checkbox"
                      checked={selected.has(item.id)}
                      onChange={() => toggle(item.id)}
                    />
                  )}
                </td>
                <td>{formatDateBR(item.postDate)}</td>
                <td>
                  {item.description}
                  {item.installmentSeq && item.installmentTotal && (
                    <span className="text-xs text-ink-tertiary ml-1">
                      ({item.installmentSeq}/{item.installmentTotal})
                    </span>
                  )}
                </td>
                <td>
                  <select
                    disabled={!isPending || patch.isPending}
                    value={item.kind}
                    onChange={(e) =>
                      patch.mutate({
                        itemId: item.id,
                        patch: { kind: e.target.value as CardInvoiceItemKind },
                      })
                    }
                  >
                    {Object.entries(KIND_LABELS).map(([value, label]) => (
                      <option key={value} value={value}>
                        {label}
                      </option>
                    ))}
                  </select>
                </td>
                <td className="text-right pr-2">
                  {item.type === 'INCOME' ? '-' : ''}
                  {formatCurrency(item.amount)}
                </td>
                <td>
                  <select
                    disabled={!isPending || patch.isPending}
                    value={item.suggestedCategoryId ?? ''}
                    onChange={(e) =>
                      patch.mutate({
                        itemId: item.id,
                        patch: { suggestedCategoryId: e.target.value || null },
                      })
                    }
                  >
                    <option value="">—</option>
                    {categories
                      .filter((c: any) => c.type === item.type)
                      .map((c: any) => (
                        <option key={c.id} value={c.id}>
                          {c.name}
                        </option>
                      ))}
                  </select>
                </td>
                <td>
                  {/* Rateio: marcar outra empresa move a despesa para a DRE dela. */}
                  <select
                    disabled={!isPending || patch.isPending}
                    value={item.beneficiaryCompanyId ?? ''}
                    onChange={(e) =>
                      patch.mutate({
                        itemId: item.id,
                        patch: { beneficiaryCompanyId: e.target.value || null },
                      })
                    }
                  >
                    <option value="">Empresa do cartão</option>
                    {otherCompanies.map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.name}
                      </option>
                    ))}
                  </select>
                </td>
                <td>
                  <Badge tone={ITEM_STATUS_TONE[item.status] ?? 'neutral'}>
                    {ITEM_STATUS_LABELS[item.status] ?? item.status}
                  </Badge>
                </td>
                <td>
                  {isPending && (
                    <Button
                      variant="ghost"
                      disabled={patch.isPending}
                      onClick={() =>
                        patch.mutate({ itemId: item.id, patch: { status: 'REJECTED' } })
                      }
                    >
                      Rejeitar
                    </Button>
                  )}
                  {item.status === 'APPROVED' && invoice.status !== 'CLOSED' && (
                    <Button
                      variant="ghost"
                      disabled={unapprove.isPending}
                      onClick={() => handleUnapprove(item.id)}
                    >
                      Desfazer
                    </Button>
                  )}
                  {item.status === 'REJECTED' && invoice.status !== 'CLOSED' && (
                    <Button
                      variant="ghost"
                      disabled={patch.isPending}
                      onClick={() => handleRestore(item.id)}
                    >
                      Restaurar
                    </Button>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>

      {otherCompanies.length > 0 && (
        <p className="mt-2 text-xs text-ink-tertiary">
          <strong>Gasto de:</strong> marcar outra empresa move a despesa para a DRE dela — o
          pagamento continua saindo do caixa desta. O saldo entre as empresas aparece no fechamento
          do mês, onde você faz a quitação.
        </p>
      )}

      <Modal
        open={showAddModal}
        onClose={() => setShowAddModal(false)}
        title="Adicionar item manual (IOF, juros, encargo esquecido)"
      >
        <form onSubmit={handleAddItem} className="space-y-4">
          <Input
            label="Data *"
            type="date"
            value={manualForm.postDate}
            onChange={(e) => setManualForm((f) => ({ ...f, postDate: e.target.value }))}
            required
          />
          <Input
            label="Descrição *"
            value={manualForm.description}
            onChange={(e) => setManualForm((f) => ({ ...f, description: e.target.value }))}
            required
          />
          <Input
            label="Valor *"
            type="number"
            step="0.01"
            min="0.01"
            value={manualForm.amount}
            onChange={(e) => setManualForm((f) => ({ ...f, amount: e.target.value }))}
            required
          />
          <div>
            <label className="block text-xs font-medium text-ink-secondary uppercase tracking-wider mb-1.5">
              Tipo de linha
            </label>
            <select
              className="h-9 w-full border border-border rounded-md px-3 text-sm bg-surface text-ink"
              value={manualForm.kind}
              onChange={(e) =>
                setManualForm((f) => ({ ...f, kind: e.target.value as CardInvoiceItemKind }))
              }
            >
              {Object.entries(KIND_LABELS).map(([value, label]) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </select>
          </div>
          <div className="flex justify-end gap-3 pt-2">
            <Button type="button" variant="ghost" onClick={() => setShowAddModal(false)}>
              Cancelar
            </Button>
            <Button type="submit" loading={addItem.isPending}>
              Adicionar
            </Button>
          </div>
        </form>
      </Modal>

      <ConciliarFaturaDialog
        open={showConciliar}
        onClose={() => setShowConciliar(false)}
        invoiceId={id}
      />
    </div>
  );
}
